import React, {Fragment} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {useSelector} from 'react-redux';
import {browserHistory, withRouter, WithRouterProps} from 'react-router';

import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import VersionInfoBar from 'app/components/general/VersionInfoBar';
import {useCurrentUserState} from 'app/current-user/api';
import {ViewModeTitle} from 'app/general/detailpage/ViewModeTitle';
import {showNoApprovedVersionsAlertBoxIfNotApproved} from 'app/general/structures/document-alerts/NoApprovedVersionsAlertBox';
import {MainContentContainer} from 'app/general/structures/MainContentContainer';
import {SPPage} from 'app/general/structures/SPPage';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {toggleProcedureAdvancedMode} from 'app/procedures/actions';
import {ProcedureHeaderOptionsDropdown} from 'app/procedures/components/ProcedureHeaderOptionsDropdown';
import {StyledStepsListDIV} from 'app/procedures/components/structures';
import {useQuiz, useQuizVersionDetail, useQuizVersions} from 'app/quizzes/api';
import {HiddenViewContent} from 'app/quizzes/components/HiddenViewContent';
import {QuizPageSidebar} from 'app/quizzes/components/QuizPageSidebar';
import {ViewContent} from 'app/quizzes/components/ViewContent';
import {ViewQuizMetadata} from 'app/quizzes/components/ViewQuizMetadata';
import {sweetAPI} from 'app/store/sweet-api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {getQuizName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {QUIZ_TAGS} from 'app/utils/rtk-tags';

interface Props
  extends WithRouterProps<{
    hashid: string;
    versionid: null | string;
  }> {
  route: {
    props: {
      showAttempts?: boolean;
    };
  };
}

export function BaseQuizPage({route, params}: Props) {
  const {versionid: versionId, hashid} = params;
  const {data: quiz} = useQuiz(hashid);
  const {data: quizVersion} = useQuizVersionDetail(
    versionId != null && quiz?.can_change ? {versionId} : skipToken
  );
  const {data: quizVersions} = useQuizVersions(
    quiz != null && quiz?.can_change ? {quizId: quiz.id} : skipToken
  );

  const user = useCurrentUserState().data!;

  const deletingDocument = useSelector(
    (state) => state.toasts.isDeletingDocument
  );
  const advancedMode = useSelector((state) =>
    state.proceduresPage.get('procedureAdvancedMode')
  );

  const actions = useAsyncActions({
    toggleProcedureAdvancedMode,
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  useModelChangeSubscription(
    (message, {runWhenAny, modelIdMatch, allRelatedMatch}) => {
      if (deletingDocument) {
        return;
      }

      if (quiz != null) {
        const expectedRelated = {
          content_type: 'quiz',
          id: quiz.id,
        };

        runWhenAny(
          message,
          [
            modelIdMatch('quiz', quiz.id),
            allRelatedMatch('quizversion', expectedRelated),
          ],
          () => {
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: QUIZ_TAGS.TAG, id: 'list'},
            ]);
          }
        );
      }
    },
    [deletingDocument, quiz, quizVersion]
  );

  if (quiz == null) {
    return null;
  }

  const advancedModeEnabled = versionId == null ? advancedMode : false;

  const takeQuiz = () => {
    if (quiz) {
      browserHistory.push(quiz.take_url);
    }
  };

  const actionButtonsProps = {
    contentType: 'quiz' as const,
    document: quiz,
    canChange: quiz?.can_change,
    additionalContent: () => (
      <ProcedureHeaderOptionsDropdown
        advancedModeEnabled={advancedModeEnabled}
        toggleAdvancedMode={actions.toggleProcedureAdvancedMode}
      />
    ),
  };

  return (
    <SPPage
      pageTitle={quiz?.name}
      horizontalPadding={false}
      breadcrumbProps={{
        docName: getQuizName(quiz?.name),
        version: quizVersion,
      }}
      actionArea={
        quiz != null ? (
          <GenericDocumentActionButtons
            pageContext="view-published"
            {...actionButtonsProps}
          />
        ) : null
      }
      sidebar={
        quiz && (
          <QuizPageSidebar
            quiz={quiz}
            versionId={versionId}
            versions={quizVersions?.results || []}
            version={quizVersion}
            userId={user.id}
            route={route}
          />
        )
      }
      versionbar={
        <>
          {quizVersion != null ? (
            <VersionInfoBar
              objectTypeName={gt('quiz')}
              version={quizVersion}
              className={'sidebar-visible'}
              showingDiff={false}
            />
          ) : null}
        </>
      }
    >
      {quiz?.id && (
        <>
          <DocumentPresenceMembers
            contentType="quiz"
            objectId={quiz?.id || ''}
            pageContext="view"
          />
          <ViewQuizMetadata
            quiz={quiz}
            takeQuiz={quiz != null ? takeQuiz : undefined}
            content={{
              name: quiz.name,
              description: quiz.description,
              pass_mark: quiz.pass_mark,
              duration: quiz.duration,
              content: quizVersion?.content || null,
            }}
            advancedModeEnabled={advancedModeEnabled}
            warningComponent={
              <Fragment>
                {showNoApprovedVersionsAlertBoxIfNotApproved(
                  'quiz',
                  quiz.approved_at
                )}
              </Fragment>
            }
          />
        </>
      )}

      <StyledStepsListDIV className="js-document-body container pt-0">
        <MainContentContainer>
          <ViewModeTitle className="px-0" title={gt('Questions')} />
          {quiz != null ? (
            quiz.can_change && quizVersion != null ? (
              <ViewContent content={quizVersion?.content} />
            ) : (
              <HiddenViewContent quiz={quiz} />
            )
          ) : null}
        </MainContentContainer>
      </StyledStepsListDIV>
    </SPPage>
  );
}

export const QuizPage = withRouter(BaseQuizPage);
